// ✅ src/pages/VehicleV2/BatteryCardMaintenanceListingForVehicleLogBook.jsx  (FULL FRONTEND)
// ASPX: BatteryCardMaintenanceListingForVehicleLogBook.aspx
//
// ✅ Keeps SAME UI/template/style pattern as your sample (HeaderBar + VehicleLayoutWrapper + skin colors)
// ✅ Implements:
// - Store dropdown
// - Vehicle RegNo + Vehicle Name autocomplete (datalist)
// - Battery Type / Battery Code dependent dropdowns (like ASPX SelectedIndexChanged)
// - Fitted Date validation (no future)
// - Vehicle Main Type dropdown
// - Battery Status dropdown
// - Search + View All
// - Add New (navigate to BatteryCardMaintenance page)
// - Grid with: Alert (Need To Change), Testing, Scrap, Select All + Delete Selected
// - Pagination (server-side)
//
// 🔁 Backend expected (you can match your SP flow):
//   GET  /api/batteryCardMaintenanceLogBook/init?branchId=8&branchType=HO&loginUserId=37
//        -> { success:true, stores:[{label,value}], batteryCodes:[{label,value}], batteryTypes:[{label,value}], vehicleMainTypes:[{label,value}] }
//
//   GET  /api/batteryCardMaintenanceLogBook/list?branchType=HO&branchId=8&storeId=...&vehNumber=...&vehName=...&batteryTypeId=...&batteryCodeId=...&fittedDate=YYYY-MM-DD&vehicleTypeIndex=...&batteryStatus=...
//        &page=1&pageSize=10
//        -> { success:true, rows:[...], total:123 }
//
//   GET  /api/batteryCardMaintenanceLogBook/ac/vehiclename?q=..&branchId=..&loginUserId=..&storeId=..
//        -> { success:true, items:[...] }
//   GET  /api/batteryCardMaintenanceLogBook/ac/vehicleregno?q=..&branchId=..&loginUserId=..&storeId=..
//        -> { success:true, items:[...] }
//
//   GET  /api/batteryCardMaintenanceLogBook/deps/byBatteryCode?batteryCodeId=..&branchId=..
//        -> { success:true, batteryTypes:[{label,value}] }
//   GET  /api/batteryCardMaintenanceLogBook/deps/byBatteryType?batteryTypeId=..&branchId=..
//        -> { success:true, batteryCodes:[{label,value}] }
//
//   POST /api/batteryCardMaintenanceLogBook/delete  body:{ ids:[batterycardmainid,...] }
//        -> { success:true, deleted: N, message? }
//   POST /api/batteryCardMaintenanceLogBook/scrap   body:{ batteryCardMainId: 123 }
//        -> { success:true, message? }
//
// ✅ Navigation (same as ASPX):
// - AddNew -> /vehicle/BatteryCardMaintenance
// - Testing -> /vehicle/BatteryTesting?batterycardmainid=...
// (If you already have routes differently, just change navigate() URLs.)

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaTrash,
  FaPlus,
  FaTools,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/batteryCardMaintenanceVLB";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isFuture = (iso) => {
  const s = toStr(iso);
  if (!s) return false;
  return new Date(s) > new Date(todayISO());
};

const normalizeOptionList = (arr, fallbackLabelKey, fallbackValueKey) => {
  const list = Array.isArray(arr) ? arr : [];
  return list.map((x) => {
    if (typeof x === "string") return { label: x, value: x };
    const label =
      x.label ??
      x[fallbackLabelKey] ??
      x.value ??
      x[fallbackValueKey] ??
      "";
    const value =
      x.value ??
      x[fallbackValueKey] ??
      x.label ??
      x[fallbackLabelKey] ??
      "";
    return { label: String(label), value: String(value) };
  });
};

export default function BatteryCardMaintenanceListingForVehicleLogBook() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // Session context (matches your other pages)
  const [userCtx] = useState(() => {
     const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
   
    return { branchId, branchType, loginUserId };
  });

  const [showFilters, setShowFilters] = useState(true);

  // Dropdowns
  const [dd, setDd] = useState({
    stores: [{ label: "Select", value: "Select" }],
    batteryCodes: [{ label: "Select", value: "Select" }],
    batteryTypes: [{ label: "Select", value: "Select" }],
    vehicleMainTypes: [{ label: "Select", value: "Select" }], // ASPX ddl_vehicletype has "index" value
  });

  // Filters
  const [filters, setFilters] = useState({
    storeId: "Select",
    vehNumber: "",
    vehName: "",
    batteryTypeId: "Select",
    batteryCodeId: "Select",
    fittedDate: "", // ISO in React; backend can accept ISO and convert to dd/MM/yyyy if needed
    vehicleTypeIndex: "Select",
    batteryStatus: "Select", // Select / Process / Scrapped
  });

  // Autocomplete
  const [ac, setAc] = useState({ regNos: [], vehicleNames: [] });

  // Grid + pagination
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Selection (delete)
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const totalPages = Math.max(1, Math.ceil(Number(total || 0) / pageSize));
  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2)
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [page, totalPages]);

  const validate = (f) => {
    if (f.fittedDate && isFuture(f.fittedDate)) {
      toast.error("Select Fitted date lesser than or equal current date");
      return false;
    }
    return true;
  };

  const loadInit = async () => {
    try {
      const params = {
        branchId: userCtx.branchId,
        branchType: userCtx.branchType,
        loginUserId: userCtx.loginUserId,
      };

      const { data } = await axios.get(`${API_BASE}/init`, { params });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      const stores = normalizeOptionList(data.stores, "fld_storename", "fld_storeid");
      const batteryCodes = normalizeOptionList(
        data.batteryCodes,
        "fld_batterycode",
        "fld_batteryid"
      );
      const batteryTypes = normalizeOptionList(
        data.batteryTypes,
        "fld_batterytype",
        "fld_batteryid"
      );
      const vehicleMainTypes = normalizeOptionList(
        data.vehicleMainTypes,
        "fld_vehicle_subtypename",
        "fld_vehicle_subtypeid"
      );

      setDd({
        stores: stores.length ? [{ label: "Select", value: "Select" }, ...stores] : [{ label: "Select", value: "Select" }],
        batteryCodes: batteryCodes.length ? [{ label: "Select", value: "Select" }, ...batteryCodes] : [{ label: "Select", value: "Select" }],
        batteryTypes: batteryTypes.length ? [{ label: "Select", value: "Select" }, ...batteryTypes] : [{ label: "Select", value: "Select" }],
        vehicleMainTypes: vehicleMainTypes.length ? [{ label: "Select", value: "Select" }, ...vehicleMainTypes] : [{ label: "Select", value: "Select" }],
      });

      // ASPX: if Session["mainvehiclebatteryid"] present, it pre-fills regno.
      // If you store that in sessionStorage, we can mimic:
      const mainVehicleRegNo = toStr(sessionStorage.getItem("mainvehregno_battery") || "");
      if (mainVehicleRegNo) {
        setFilters((p) => ({ ...p, vehNumber: mainVehicleRegNo }));
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load init");
    }
  };

  const loadAC = async (kind, q) => {
    try {
      const s = toStr(q);
      if (s.length < 1) return;

      const params = {
        branchId: userCtx.branchId,
        loginUserId: userCtx.loginUserId,
        storeId: isSelect(filters.storeId) ? "Select" : filters.storeId,
        q: s,
      };

      const url =
        kind === "vehiclename"
          ? `${API_BASE}/ac/vehiclename`
          : `${API_BASE}/ac/vehicleregno`;

      const { data } = await axios.get(url, { params });
      if (!data?.success) return;

      const items = Array.isArray(data.items) ? data.items.map((x) => String(x)) : [];
      setAc((p) =>
        kind === "vehiclename" ? { ...p, vehicleNames: items } : { ...p, regNos: items }
      );
    } catch {
      // ignore
    }
  };

  // Dependent dropdowns (like SelectedIndexChanged in ASPX)
  const loadBatteryTypesByCode = async (batteryCodeId) => {
    try {
      if (!batteryCodeId || isSelect(batteryCodeId)) return;

      const params = { branchId: userCtx.branchId, batteryCodeId };
      const { data } = await axios.get(`${API_BASE}/deps/byBatteryCode`, { params });
      if (!data?.success) return;

      const batteryTypes = normalizeOptionList(
        data.batteryTypes,
        "fld_batterytype",
        "fld_batteryid"
      );

      setDd((p) => ({
        ...p,
        batteryTypes: [{ label: "Select", value: "Select" }, ...batteryTypes],
      }));
    } catch {
      // ignore
    }
  };

  const loadBatteryCodesByType = async (batteryTypeId) => {
    try {
      if (!batteryTypeId || isSelect(batteryTypeId)) return;

      const params = { branchId: userCtx.branchId, batteryTypeId };
      const { data } = await axios.get(`${API_BASE}/deps/byBatteryType`, { params });
      if (!data?.success) return;

      const batteryCodes = normalizeOptionList(
        data.batteryCodes,
        "fld_batterycode",
        "fld_batteryid"
      );

      setDd((p) => ({
        ...p,
        batteryCodes: [{ label: "Select", value: "Select" }, ...batteryCodes],
      }));
    } catch {
      // ignore
    }
  };

  const loadList = async (overrides = {}) => {
    try {
      const f = { ...filters, ...overrides };
      if (!validate(f)) return;

      const params = {
        branchType: userCtx.branchType,
        branchId: userCtx.branchId,

        storeId: isSelect(f.storeId) ? "Select" : f.storeId,
        vehNumber: toStr(f.vehNumber),
        vehName: toStr(f.vehName),

        batteryTypeId: isSelect(f.batteryTypeId) ? "Select" : f.batteryTypeId,
        batteryCodeId: isSelect(f.batteryCodeId) ? "Select" : f.batteryCodeId,

        fittedDate: toStr(f.fittedDate), // ISO (backend can convert to dd/MM/yyyy)
        vehicleTypeIndex: isSelect(f.vehicleTypeIndex) ? "Select" : f.vehicleTypeIndex,
        batteryStatus: isSelect(f.batteryStatus) ? "Select" : f.batteryStatus,

        page,
        pageSize,
      };

      const { data } = await axios.get(`${API_BASE}/list`, { params });
      if (!data?.success) throw new Error(data?.message || "List failed");

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));

      // clear selections on new data
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load list");
      setRows([]);
      setTotal(0);
      setSelectedIds(new Set());
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleStoreChange = async (val) => {
    setFilters((p) => ({ ...p, storeId: val }));
    // Optional: refresh autocomplete results
    setAc({ regNos: [], vehicleNames: [] });
  };

  const handleBatteryCodeChange = async (val) => {
    setFilters((p) => ({ ...p, batteryCodeId: val, batteryTypeId: "Select" }));
    if (!isSelect(val)) await loadBatteryTypesByCode(val);
    else {
      // if user resets to Select, reload init batteryTypes (simple approach)
      await loadInit();
    }
  };

  const handleBatteryTypeChange = async (val) => {
    setFilters((p) => ({ ...p, batteryTypeId: val, batteryCodeId: "Select" }));
    if (!isSelect(val)) await loadBatteryCodesByType(val);
    else {
      await loadInit();
    }
  };

  const handleSearch = async () => {
    setPage(1);
    await loadList({});
    toast.success("Search completed");
  };

  const handleViewAll = async () => {
    const reset = {
      storeId: "Select",
      vehNumber: "",
      vehName: "",
      batteryTypeId: "Select",
      batteryCodeId: "Select",
      fittedDate: "",
      vehicleTypeIndex: "Select",
      batteryStatus: "Select",
    };
    setFilters(reset);
    await loadInit();
    setPage(1);
    await loadList(reset);
    toast.info("Showing all records");
  };

  const handleAddNew = () => {
    // ASPX: Session.Remove("batterycardmainid"); Response.Redirect("BatteryCardMaintenance.aspx");
    sessionStorage.removeItem("batterycardmainid");
    navigate("/vehicle/BatteryCardMaintenance");
  };

  // Selection helpers
  const allSelectableIds = useMemo(() => {
    return rows
      .filter((r) => toStr(r.fld_batterystatus) !== "Scrapped")
      .map((r) => String(r.fld_batterycardmainid ?? r.BatteryCardMainId ?? ""));
  }, [rows]);

  const isAllChecked =
    allSelectableIds.length > 0 &&
    allSelectableIds.every((id) => selectedIds.has(String(id)));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllChecked) {
        allSelectableIds.forEach((id) => next.delete(String(id)));
      } else {
        allSelectableIds.forEach((id) => next.add(String(id)));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    const sid = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Please select any one...");
      return;
    }
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      const { data } = await axios.post(`${API_BASE}/delete`, { ids });
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data?.message || `Deleted ${data?.deleted ?? ids.length} record(s)`);
      await loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Delete failed");
    }
  };

  const handleScrap = async (row) => {
    const id = row?.fld_batterycardmainid ?? row?.BatteryCardMainId;
    if (!id) return;

    if (!window.confirm("Do you really want to Scrap?")) return;

    try {
      const { data } = await axios.post(`${API_BASE}/scrap`, {
        batteryCardMainId: Number(id),
      });
      if (!data?.success) throw new Error(data?.message || "Scrap failed");
      toast.success(data?.message || "Battery card Scrapped successfully");
      await loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Scrap failed");
    }
  };

  const handleTesting = (row) => {
    const id = row?.fld_batterycardmainid ?? row?.BatteryCardMainId;
    if (!id) return;

    // mimic ASPX Session["batterycardmainid"] + redirect
    sessionStorage.setItem("batterycardmainid", String(id));
    navigate(`/vehicle/BatteryTesting?batterycardmainid=${encodeURIComponent(id)}`);
  };

  // Alert text logic (ASPX: if currentlife+15 >= actuallife => "Need To Change")
  const computeAlert = (row) => {
    const actual = Number(row?.fld_actuallife ?? row?.ActualLife ?? 0);
    const current = Number(row?.fld_currentlife ?? row?.CurrentLife ?? 0);
    if (!Number.isFinite(actual) || !Number.isFinite(current)) return "";
    if (current + 15 >= actual) return "Need To Change";
    return "";
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD MAINTENANCE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Battery Card Maintenance Listing
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={(e) => handleStoreChange(e.target.value)}
                    className={inputClass}
                  >
                    {dd.stores.map((x) => (
                      <option key={`${x.value}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    list="acRegNo"
                    name="vehNumber"
                    value={filters.vehNumber}
                    onChange={(e) => {
                      handleChange(e);
                      loadAC("vehicleregno", e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle regno"
                  />
                  <datalist id="acRegNo">
                    {ac.regNos.map((x, i) => (
                      <option key={i} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    list="acVehName"
                    name="vehName"
                    value={filters.vehName}
                    onChange={(e) => {
                      handleChange(e);
                      loadAC("vehiclename", e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                  <datalist id="acVehName">
                    {ac.vehicleNames.map((x, i) => (
                      <option key={i} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Battery Type</label>
                  <select
                    name="batteryTypeId"
                    value={filters.batteryTypeId}
                    onChange={(e) => handleBatteryTypeChange(e.target.value)}
                    className={inputClass}
                  >
                    {dd.batteryTypes.map((x) => (
                      <option key={`${x.value}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Code</label>
                  <select
                    name="batteryCodeId"
                    value={filters.batteryCodeId}
                    onChange={(e) => handleBatteryCodeChange(e.target.value)}
                    className={inputClass}
                  >
                    {dd.batteryCodes.map((x) => (
                      <option key={`${x.value}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Fitted Date</label>
                  <input
                    type="date"
                    name="fittedDate"
                    value={filters.fittedDate}
                    onChange={handleChange}
                    className={inputClass}
                    max={todayISO()}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleTypeIndex"
                    value={filters.vehicleTypeIndex}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {dd.vehicleMainTypes.map((x) => (
                      <option key={`${x.value}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Status</label>
                  <select
                    name="batteryStatus"
                    value={filters.batteryStatus}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    <option value="Process">Process</option>
                    <option value="Scrapped">Scrap</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col md:flex-row md:justify-center gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSearch}
                  className={`flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search
                </button>

                <button
                  onClick={handleViewAll}
                  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <FaEye />
                  View All
                </button>

                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <FaTrash />
                  Delete Selected
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your filters or click "View All".
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-left font-semibold">Store</th>
                      <th className="px-4 py-4 text-left font-semibold">Vehicle Main Type</th>
                      <th className="px-4 py-4 text-left font-semibold">Vehicle Code</th>
                      <th className="px-4 py-4 text-left font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-left font-semibold">Vehicle Reg No</th>
                      <th className="px-4 py-4 text-left font-semibold">Battery Code</th>
                      <th className="px-4 py-4 text-left font-semibold">Battery Type</th>
                      <th className="px-4 py-4 text-left font-semibold">Fitted Date</th>
                      <th className="px-4 py-4 text-left font-semibold">Current/Remove Date</th>
                      <th className="px-4 py-4 text-left font-semibold">Actual Life</th>
                      <th className="px-4 py-4 text-left font-semibold">Current Life</th>
                      <th className="px-4 py-4 text-left font-semibold">Alert</th>
                      <th className="px-4 py-4 text-center font-semibold">Testing</th>
                      <th className="px-4 py-4 text-center font-semibold">Scrap</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            checked={isAllChecked}
                            onChange={toggleSelectAll}
                          />
                          <span>Select All</span>
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const id = r.fld_batterycardmainid ?? r.BatteryCardMainId;
                      const sid = String(id ?? "");
                      const scrapped = toStr(r.fld_batterystatus) === "Scrapped";
                      const alertText = computeAlert(r);

                      const rowBg = scrapped
                        ? "bg-rose-200/70"
                        : isDark
                        ? "bg-gray-800"
                        : "bg-white";

                      return (
                        <tr
                          key={sid || idx}
                          className={`${rowBg} ${
                            scrapped ? "opacity-70" : isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                          } transition-colors duration-200`}
                          style={scrapped ? { pointerEvents: "none" } : {}}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_storename ?? r.Store ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.VehicleType ?? r.vehicleMainType ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_vehicle_code ?? r.VehicleCode ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_vehicle_name ?? r.VehicleName ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_vehicle_regno ?? r.RegNo ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_batterycode ?? r.BatteryCode ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_batterytype ?? r.BatteryType ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Fitteddate ?? r.FittedDate ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.currentdate ?? r.CurrentRemoveDate ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_actuallife ?? r.ActualLife ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_currentlife ?? r.CurrentLife ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm font-semibold ${alertText ? "text-red-600" : isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {alertText}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => handleTesting(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white font-semibold shadow ${buttonClass} ${hoverClass}`}
                              title="Battery Testing"
                            >
                              <FaTools />
                              Testing
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => handleScrap(r)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white font-semibold shadow bg-orange-600 hover:bg-orange-700"
                              title="Scrap"
                            >
                              Scrap
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(sid)}
                              onChange={() => toggleRow(sid)}
                              disabled={scrapped}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Page {page} of {totalPages} — Showing {(page - 1) * pageSize + 1} to{" "}
                {Math.min(page * pageSize, total)} of {total} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    page === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {pageButtons.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 border rounded text-sm ${
                      page === p
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    page === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
